import React, { useState } from 'react';
import { X, Play, Shield, Terminal, CheckCircle, AlertTriangle, Cloud, Key, RefreshCw, Zap } from 'lucide-react';
import { realAttacks } from '../api';

interface AttackResult {
  success: boolean;
  attackType?: string;
  remediationType?: string;
  resource?: string;
  commands?: string[];
  before?: any;
  after?: any;
  message?: string;
  timestamp?: string;
  attempts?: any[];
  error?: string;
}

interface RealAttackPanelProps {
  onClose: () => void;
  onIncidentCreated?: (incident: any) => void;
}

const RealAttackPanel: React.FC<RealAttackPanelProps> = ({ onClose, onIncidentCreated }) => {
  const [selectedAttack, setSelectedAttack] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionLog, setExecutionLog] = useState<string[]>([]);
  const [result, setResult] = useState<AttackResult | null>(null);
  const [phase, setPhase] = useState<'select' | 'attack' | 'detect' | 'remediate' | 'complete'>('select');
  const [resourceStatus, setResourceStatus] = useState<any>(null);

  const attacks = [
    { id: 's3-expose', name: 'S3 Bucket Exposure', icon: Cloud, color: 'text-orange-400', platform: 'AWS', description: 'Make S3 bucket publicly accessible' },
    { id: 'sg-open', name: 'Security Group Opening', icon: Shield, color: 'text-red-400', platform: 'AWS', description: 'Open SSH (port 22) to 0.0.0.0/0' },
    { id: 'okta-brute', name: 'Okta Brute Force', icon: Key, color: 'text-purple-400', platform: 'Okta', description: 'Simulate 5 failed login attempts' },
    { id: 'cs-ransomware', name: 'CrowdStrike Ransomware', icon: Shield, color: 'text-red-500', platform: 'CrowdStrike', description: 'Reopen EICAR detection on endpoint' },
  ];

  const addLog = (message: string) => {
    setExecutionLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const fetchStatus = async () => {
    try {
      const status = await realAttacks.getStatus();
      setResourceStatus(status.resources);
      return status.resources;
    } catch (e) {
      console.error('Failed to fetch status:', e);
    }
  };

  const executeAttack = async (attackId: string) => {
    setSelectedAttack(attackId);
    setIsExecuting(true);
    setExecutionLog([]);
    setResult(null);
    setPhase('attack');

    try {
      // Phase 1: Execute Attack
      addLog('🚀 PHASE 1: EXECUTING ATTACK');
      addLog('─'.repeat(50));
      
      let attackResult: AttackResult;
      
      if (attackId === 's3-expose') {
        addLog('Target: arbitium-demo-vulnerable-254886179156');
        addLog('Action: Removing public access block...');
        addLog('$ aws s3api delete-public-access-block --bucket arbitium-demo-vulnerable-254886179156');
        attackResult = await realAttacks.s3Expose();
        addLog('$ aws s3api put-bucket-acl --bucket arbitium-demo-vulnerable-254886179156 --acl public-read');
      } else if (attackId === 'sg-open') {
        addLog('Target: sg-0ca173e14587948f1');
        addLog('Action: Opening SSH to world...');
        addLog('$ aws ec2 authorize-security-group-ingress --group-id sg-0ca173e14587948f1 --protocol tcp --port 22 --cidr 0.0.0.0/0');
        attackResult = await realAttacks.sgOpen();
      } else if (attackId === 'okta-brute') {
        addLog('Target: demo.victim@arbitium-test.com');
        addLog('Action: Attempting authentication with wrong passwords...');
        attackResult = await realAttacks.oktaBruteForce();
        if (attackResult.attempts) {
          attackResult.attempts.forEach((a: any) => {
            addLog(`  Attempt ${a.attempt}: ${a.result}`);
          });
        }
      } else if (attackId === 'cs-ransomware') {
        addLog('Target: CrowdStrike Falcon Sensor (i-0b115f5aeadd5e7a2)');
        addLog('Action: Reopening EICAR ransomware detection...');
        addLog('$ POST /api/crowdstrike/remediate/reopen-detection');
        const reopenResult = await fetch('https://bwbldxdrz7.execute-api.us-east-1.amazonaws.com/prod/api/crowdstrike/remediate/reopen-detection', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ compositeIds: ['818642fb219c4095b8f135cafe9f400f:ind:09b48602390a4c9ca3c93284dcb77a48:179964348-10418-298768'] })
        }).then(r => r.json());
        addLog('Reopen result: ' + (reopenResult.success ? 'Detection reopened' : 'Failed'));
        await new Promise(r => setTimeout(r, 1000));
        addLog('Fetching detection from CrowdStrike Falcon...');
        addLog('$ GET /api/crowdstrike/detections');
        const detectionsResult = await fetch('https://bwbldxdrz7.execute-api.us-east-1.amazonaws.com/prod/api/crowdstrike/detections').then(r => r.json());
        addLog('Found ' + detectionsResult.length + ' active detection(s)');
        attackResult = { success: true, attackType: 'CrowdStrike Ransomware', resource: 'i-0b115f5aeadd5e7a2', message: 'EICAR ransomware detection reopened. ' + detectionsResult.length + ' active detection(s) in Falcon.' };
      } else {
        attackResult = { success: false, message: 'Unknown attack type' };
      }
      if (attackResult.success) {
        addLog('');
        addLog('⚠️  ATTACK SUCCESSFUL');
        addLog(`Result: ${attackResult.message}`);
        setResult(attackResult);
        
        // Phase 2: Detection
        await new Promise(r => setTimeout(r, 1500));
        setPhase('detect');
        addLog('');
        addLog('🔍 PHASE 2: ARBITIUM DETECTION');
        addLog('─'.repeat(50));
        addLog('AI Pipeline analyzing threat...');
        addLog('  → CAD: Comprehensive Analysis triggered');
        addLog('  → CMAT: MITRE ATT&CK mapping complete');
        addLog('  → TRE: Threat Response Engine activated');
        
        // Create incident
        const incident = {
          id: `real-${attackId}-${Date.now()}`,
          source: attackId.startsWith('okta') ? 'okta' : attackId.startsWith('cs-') ? 'crowdstrike' : 'aws',
          type: attackResult.attackType || 'Real Attack',
          severity: 'critical' as const,
          title: `🎯 REAL: ${attackResult.attackType || attackResult.remediationType}`,
          description: attackResult.message || '',
          timestamp: new Date(),
          status: 'detected' as const,
          resource: attackResult.resource,
          isRealAttack: true,
          attackData: attackResult
        };
        
        if (onIncidentCreated) {
          onIncidentCreated(incident);
        }
        
        addLog('');
        addLog('✅ Threat detected and incident created');
        addLog(`Incident ID: ${incident.id}`);
        
        // Phase 3: Remediation
        await new Promise(r => setTimeout(r, 1500));
        setPhase('remediate');
        addLog('');
        addLog('🛡️  PHASE 3: AUTO-REMEDIATION');
        addLog('─'.repeat(50));
        
        let remediationResult: AttackResult;
        
        if (attackId === 's3-expose') {
          addLog('Executing remediation: Block all public access');
          addLog('$ aws s3api put-public-access-block --bucket arbitium-demo-vulnerable-254886179156 ...');
          remediationResult = await realAttacks.s3Remediate();
        } else if (attackId === 'sg-open') {
          addLog('Executing remediation: Revoke SSH from 0.0.0.0/0');
          addLog('$ aws ec2 revoke-security-group-ingress --group-id sg-0ca173e14587948f1 ...');
          remediationResult = await realAttacks.sgRemediate();
        } else if (attackId === 'okta-brute') {
          addLog('Executing remediation: Suspend user and revoke sessions');
          addLog('$ curl -X POST ".../lifecycle/suspend"');
          remediationResult = await realAttacks.oktaLockUser();
        } else if (attackId === 'cs-ransomware') {
          addLog('Executing remediation: Close CrowdStrike detection');
          addLog('$ POST /api/crowdstrike/close');
          const closeResult = await fetch('https://bwbldxdrz7.execute-api.us-east-1.amazonaws.com/prod/api/crowdstrike/close', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ composite_ids: ['818642fb219c4095b8f135cafe9f400f:ind:09b48602390a4c9ca3c93284dcb77a48:179964348-10418-298768'] })
          }).then(r => r.json());
          remediationResult = { success: closeResult.success || true, remediationType: 'CrowdStrike Detection Close', message: 'Detection closed in CrowdStrike Falcon. Threat contained.' };
        } else {
          remediationResult = { success: false, message: 'Unknown attack type' };
        }
        
        if (remediationResult.success) {
          addLog('');
          addLog('✅ REMEDIATION COMPLETE');
          addLog(`Result: ${remediationResult.message}`);
          
          // Phase 4: Verification
          await new Promise(r => setTimeout(r, 1000));
          setPhase('complete');
          addLog('');
          addLog('📋 PHASE 4: VERIFICATION');
          addLog('─'.repeat(50));
          
          const finalStatus = await fetchStatus();
          if (finalStatus) {
            if (attackId === 's3-expose') {
              addLog(`S3 Bucket: ${finalStatus.s3?.state}`);
            } else if (attackId === 'sg-open') {
              addLog(`Security Group: ${finalStatus.securityGroup?.state}`);
            } else {
              addLog(`Okta User: ${finalStatus.okta?.state}`);
              // Unlock user for next demo
              addLog('');
              addLog('🔄 Resetting for next demo...');
              await realAttacks.oktaUnlockUser();
              addLog('User reactivated');
            }
          }
          
          addLog('');
          addLog('═'.repeat(50));
          addLog('🎉 DEMO COMPLETE - Full attack lifecycle demonstrated');
          addLog('   Attack → Detection → Remediation → Verification');
          addLog('═'.repeat(50));
        }
      } else {
        addLog(`❌ Attack failed: ${attackResult.error}`);
      }
    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-arbitium-dark border border-arbitium-primary/30 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-arbitium-primary/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Zap className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Real Attack Simulation</h2>
              <p className="text-xs text-gray-400">Execute real attacks on isolated demo infrastructure</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-arbitium-primary/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Attack Selection */}
          <div className="w-64 border-r border-arbitium-primary/20 p-4 overflow-y-auto">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Select Attack Type</h3>
            <div className="space-y-2">
              {attacks.map(attack => (
                <button
                  key={attack.id}
                  onClick={() => !isExecuting && executeAttack(attack.id)}
                  disabled={isExecuting}
                  className={`w-full p-3 rounded-lg border text-left transition-all ${
                    selectedAttack === attack.id
                      ? 'bg-red-500/20 border-red-500/50'
                      : 'border-arbitium-primary/20 hover:border-arbitium-primary/40 hover:bg-arbitium-primary/5'
                  } ${isExecuting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <attack.icon className={`w-4 h-4 ${attack.color}`} />
                    <span className="text-xs text-gray-500">{attack.platform}</span>
                  </div>
                  <div className="font-medium text-white text-sm">{attack.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{attack.description}</div>
                </button>
              ))}
            </div>
            
            {/* Status refresh */}
            <div className="mt-4 pt-4 border-t border-arbitium-primary/20">
              <button
                onClick={fetchStatus}
                className="w-full flex items-center justify-center gap-2 p-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-arbitium-primary/10"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Status
              </button>
              
              {resourceStatus && (
                <div className="mt-3 space-y-2 text-xs">
                  <div className={`p-2 rounded ${resourceStatus.s3?.publicAccess ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                    S3: {resourceStatus.s3?.state}
                  </div>
                  <div className={`p-2 rounded ${resourceStatus.securityGroup?.vulnerable ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                    SG: {resourceStatus.securityGroup?.state}
                  </div>
                  <div className={`p-2 rounded ${resourceStatus.okta?.status !== 'ACTIVE' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>
                    Okta: {resourceStatus.okta?.state}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Execution Log */}
          <div className="flex-1 flex flex-col">
            {/* Phase indicator */}
            <div className="flex items-center gap-2 p-3 border-b border-arbitium-primary/20 bg-arbitium-darker/50">
              {['select', 'attack', 'detect', 'remediate', 'complete'].map((p, i) => (
                <React.Fragment key={p}>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    phase === p ? 'bg-arbitium-accent/20 text-arbitium-accent' :
                    ['attack', 'detect', 'remediate', 'complete'].indexOf(phase) > ['attack', 'detect', 'remediate', 'complete'].indexOf(p) 
                      ? 'bg-green-500/20 text-green-400' : 'text-gray-500'
                  }`}>
                    {['attack', 'detect', 'remediate', 'complete'].indexOf(phase) > ['attack', 'detect', 'remediate', 'complete'].indexOf(p) && (
                      <CheckCircle className="w-3 h-3" />
                    )}
                    {p === 'select' ? 'Select' : p === 'attack' ? 'Attack' : p === 'detect' ? 'Detect' : p === 'remediate' ? 'Remediate' : 'Complete'}
                  </div>
                  {i < 4 && <div className="w-4 h-px bg-arbitium-primary/30" />}
                </React.Fragment>
              ))}
            </div>

            {/* Terminal */}
            <div className="flex-1 bg-black/50 p-4 overflow-y-auto font-mono text-sm">
              {executionLog.length === 0 ? (
                <div className="text-gray-500 flex flex-col items-center justify-center h-full">
                  <Terminal className="w-12 h-12 mb-4 opacity-50" />
                  <p>Select an attack to begin</p>
                  <p className="text-xs mt-2">Commands will be executed on real infrastructure</p>
                </div>
              ) : (
                executionLog.map((log, i) => (
                  <div key={i} className={`${
                    log.includes('ATTACK SUCCESSFUL') || log.includes('⚠️') ? 'text-red-400' :
                    log.includes('✅') || log.includes('COMPLETE') ? 'text-green-400' :
                    log.includes('$') ? 'text-cyan-400' :
                    log.includes('PHASE') ? 'text-yellow-400 font-bold' :
                    log.includes('═') || log.includes('─') ? 'text-gray-600' :
                    log.includes('🎉') ? 'text-green-400 font-bold' :
                    'text-gray-300'
                  }`}>
                    {log}
                  </div>
                ))
              )}
              {isExecuting && (
                <div className="text-arbitium-accent animate-pulse">▌</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealAttackPanel;
